// src/pages/project/ProjectWorkspacePage.tsx
// Project workspace hub. Tabs over the project's own domain (board, backlog, sprints,
// epics, milestones, timeline, analytics, overview); item detail opens in the right
// rail; a saved-views bar sits above the board/backlog. Wires the project service's
// own endpoints — not the task views.

import { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { observer } from '@react';
import { useProject, useI18n } from '@react/hooks';
import { Button, Badge, Alert, ErrorAlert } from '@react/components';

import {
  projectStatusMap,
  methodologyMap,
  getStatusMapping,
  getInitials,
  getAvatarColor,
  getProjectName,
  formatProgress,
} from '@/features/project/logic';
import { OverviewPanel } from '@/features/project/OverviewPanel';
import { BoardPanel } from '@/features/project/BoardPanel';
import { BacklogPanel } from '@/features/project/BacklogPanel';
import { SprintsPanel } from '@/features/project/SprintsPanel';
import { EpicsPanel } from '@/features/project/EpicsPanel';
import { MilestonesPanel } from '@/features/project/MilestonesPanel';
import { TimelinePanel } from '@/features/project/TimelinePanel';
import { AnalyticsPanel } from '@/features/project/AnalyticsPanel';
import { SavedViewsBar } from '@/features/project/SavedViewsBar';
import { ItemDetailRail } from '@/features/project/ItemDetailRail';
import { ItemKeyLookup } from '@/features/project/ItemKeyLookup';
import type { Entity } from '@core/types/entity';
import type { ProjectItem, BoardColumn, SavedView } from '@core/types/project';

type WorkspaceTab =
  | 'overview'
  | 'board'
  | 'backlog'
  | 'sprints'
  | 'epics'
  | 'milestones'
  | 'timeline'
  | 'analytics';

const TABS: { id: WorkspaceTab; labelKey: string; iconPath: string }[] = [
  { id: 'overview', labelKey: 'project.tab.overview', iconPath: 'M4 5h16M4 12h16M4 19h10' },
  { id: 'board', labelKey: 'project.tab.board', iconPath: 'M9 4h6v16H9zM3 4h6v10H3zM15 4h6v12h-6z' },
  { id: 'backlog', labelKey: 'project.tab.backlog', iconPath: 'M4 6h16M4 10h16M4 14h16M4 18h16' },
  { id: 'sprints', labelKey: 'project.tab.sprints', iconPath: 'M13 10V3L4 14h7v7l9-11h-7z' },
  { id: 'epics', labelKey: 'project.tab.epics', iconPath: 'M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z' },
  { id: 'milestones', labelKey: 'project.tab.milestones', iconPath: 'M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2z' },
  { id: 'timeline', labelKey: 'project.tab.timeline', iconPath: 'M3 12h4l3-9 4 18 3-9h4' },
  { id: 'analytics', labelKey: 'project.tab.analytics', iconPath: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
];

const VALID = new Set<string>(TABS.map((tab) => tab.id));
function resolveTab(raw: string | null): WorkspaceTab {
  return raw && VALID.has(raw) ? (raw as WorkspaceTab) : 'overview';
}

export const ProjectWorkspacePage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const store = useProject();
  const { t } = useI18n();

  const activeTab = resolveTab(searchParams.get('tab'));
  const [selectedItem, setSelectedItem] = useState<ProjectItem | null>(null);
  const [selectedColumns, setSelectedColumns] = useState<BoardColumn[]>([]);
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const setActiveTab = useCallback(
    (tab: WorkspaceTab) => {
      const next = new URLSearchParams(searchParams);
      if (tab === 'overview') next.delete('tab');
      else next.set('tab', tab);
      setSearchParams(next, { replace: true });
      setSelectedItem(null);
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    if (id) store.GetProject(id);
    return () => store.clearCurrent();
  }, [id]);

  const item = store.currentItem as never as {
    entity?: Entity;
    name?: string;
    key?: string;
    icon?: string;
    color?: string;
    status?: string;
    methodology?: string;
    description?: string;
    item_count?: number;
    completed_count?: number;
  } | null;

  if (!item && (store.isLoading || store.isLoadingItem)) {
    return <PageSkeleton />;
  }
  if (store.error && !item) {
    return (
      <div className="space-y-4">
        <ErrorAlert error={store.error} />
        <Link to="/projects" className="inline-block text-sm text-primary underline">
          {t('common.backToList')}
        </Link>
      </div>
    );
  }
  if (!item || !item.entity) {
    return (
      <Alert variant="warning" title={t('common.notFound')}>
        <Link to="/projects" className="mt-2 inline-block text-sm text-primary underline">
          {t('common.backToList')}
        </Link>
      </Alert>
    );
  }

  const projectEntity = item.entity;
  const projectName = getProjectName(item) || t('project.details');
  const status = getStatusMapping(projectStatusMap, item.status);
  const methodology = item.methodology ? methodologyMap[item.methodology] : null;
  const progress = formatProgress(item.completed_count ?? 0, item.item_count ?? 0);

  const selectItem = (it: ProjectItem, columns: BoardColumn[]) => {
    setSelectedItem(it);
    setSelectedColumns(columns);
  };
  const bumpRefresh = () => setRefreshToken((n) => n + 1);

  const applyView = (view: SavedView) => {
    setActiveViewId(view.entity?.id ?? null);
    const type = view.type;
    if (type === 'SAVED_VIEW_TYPE_BOARD') setActiveTab('board');
    else if (type === 'SAVED_VIEW_TYPE_TIMELINE') setActiveTab('timeline');
    else setActiveTab('backlog');
  };

  const showViewsBar = activeTab === 'board' || activeTab === 'backlog';

  return (
    <div className="flex gap-4">
      <div className="min-w-0 flex-1 space-y-6">
        {/* Breadcrumb */}
        <nav className="text-sm" aria-label={t('project.breadcrumb')}>
          <Link to="/projects" className="text-muted-foreground transition-colors hover:text-foreground">
            {t('project.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{projectName}</span>
        </nav>

        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            {item.icon ? (
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-muted text-xl">
                {item.icon}
              </div>
            ) : (
              <div
                className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg text-white"
                style={{ backgroundColor: item.color || getAvatarColor(projectName) }}
              >
                <span className="text-base font-semibold">{getInitials(projectName)}</span>
              </div>
            )}
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="truncate text-xl font-bold text-foreground">{projectName}</h1>
                {item.key && (
                  <span className="flex-shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                    {item.key}
                  </span>
                )}
                <Badge variant={status.variant as never} size="sm">
                  {t(status.labelKey)}
                </Badge>
                {methodology && (
                  <Badge variant="primary" size="sm">
                    {t(methodology.labelKey)}
                  </Badge>
                )}
              </div>
              {item.description && (
                <p className="mt-0.5 max-w-xl truncate text-sm text-muted-foreground">{item.description}</p>
              )}
            </div>
          </div>

          <div className="flex flex-shrink-0 items-center gap-2">
            <ItemKeyLookup
              projectEntity={projectEntity}
              onfound={(found) => {
                setSelectedItem(found);
                setSelectedColumns([]);
              }}
            />
            {(item.item_count ?? 0) > 0 && (
              <div className="mr-2 hidden items-center gap-2 sm:flex">
                <div className="h-1.5 w-16 overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-primary" style={{ width: `${progress}%` }} />
                </div>
                <span className="text-xs text-muted-foreground">{progress}%</span>
              </div>
            )}
            <Link to={`/projects/${projectEntity.id}/settings`}>
              <Button variant="secondary" size="sm">
                {t('project.settings')}
              </Button>
            </Link>
          </div>
        </div>

        {/* Tabs */}
        <div className="-mx-1 border-b border-border">
          <div className="scrollbar-none flex gap-0 overflow-x-auto px-1" role="tablist" aria-label={t('project.workspace.tabs')}>
            {TABS.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                    isActive
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground'
                  }`}
                >
                  <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d={tab.iconPath} />
                  </svg>
                  <span>{t(tab.labelKey)}</span>
                </button>
              );
            })}
          </div>
        </div>

        {showViewsBar && (
          <SavedViewsBar projectEntity={projectEntity} activeViewId={activeViewId} onApply={applyView} />
        )}

        {/* Tab content */}
        <div role="tabpanel">
          {activeTab === 'overview' && <OverviewPanel projectEntity={projectEntity} />}
          {activeTab === 'board' && (
            <BoardPanel
              projectEntity={projectEntity}
              selectedItemId={selectedItem?.entity?.id ?? null}
              onSelectItem={selectItem}
              onItemsChanged={bumpRefresh}
              refreshToken={refreshToken}
            />
          )}
          {activeTab === 'backlog' && (
            <BacklogPanel
              projectEntity={projectEntity}
              selectedItemId={selectedItem?.entity?.id ?? null}
              onSelectItem={selectItem}
              onItemsChanged={bumpRefresh}
              refreshToken={refreshToken}
            />
          )}
          {activeTab === 'sprints' && <SprintsPanel projectEntity={projectEntity} onChanged={bumpRefresh} />}
          {activeTab === 'epics' && <EpicsPanel projectEntity={projectEntity} onChanged={bumpRefresh} />}
          {activeTab === 'milestones' && <MilestonesPanel projectEntity={projectEntity} onChanged={bumpRefresh} />}
          {activeTab === 'timeline' && <TimelinePanel projectEntity={projectEntity} />}
          {activeTab === 'analytics' && <AnalyticsPanel projectEntity={projectEntity} />}
        </div>
      </div>

      {/* Item detail rail */}
      {selectedItem && (
        <div className="fixed inset-y-0 right-0 z-40 w-full max-w-sm sm:static sm:z-auto sm:h-auto sm:w-80 sm:flex-shrink-0">
          <div className="h-full sm:sticky sm:top-4 sm:h-[calc(100vh-2rem)]">
            <ItemDetailRail
              projectEntity={projectEntity}
              item={selectedItem}
              columns={selectedColumns}
              onClose={() => setSelectedItem(null)}
              onChanged={bumpRefresh}
            />
          </div>
        </div>
      )}
    </div>
  );
});

function PageSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-4 w-32 animate-pulse rounded bg-muted" />
      <div className="flex items-center gap-4">
        <div className="h-12 w-12 animate-pulse rounded-lg bg-muted" />
        <div className="space-y-2">
          <div className="h-5 w-48 animate-pulse rounded bg-muted" />
          <div className="h-4 w-32 animate-pulse rounded bg-muted" />
        </div>
      </div>
      <div className="flex gap-4 border-b border-border pb-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-4 w-16 animate-pulse rounded bg-muted" />
        ))}
      </div>
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-10 w-full animate-pulse rounded bg-muted" />
        ))}
      </div>
    </div>
  );
}

export default ProjectWorkspacePage;
